/// <amd-module name="Release/Landing/LeftPanel/AllPipelines/AllDefinitionsTree" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { localeFormat } from "VSS/Core/Util/String";
import { css } from "VSSUI/Util";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";

import { SimpleTableCell, ITableColumn } from "VSSUI/Table";
import { Icon, IIconProps } from "VSSUI/Icon";
import { Tree as TreeEx, ITreeColumn, TreeExpand } from "VSSUI/TreeEx";
import { Tooltip } from "VSSUI/TooltipEx";
import { ITreeItemEx, ITreeItemProvider as ITreeItemExProvider } from "VSSUI/Utilities/TreeItemProvider";
import { ListSelection } from "VSSUI/List";
import { Components, VssComponent, IVssComponentContext, IVssComponentProperties } from "VSS/Platform/Layout";
import { IVssHistoryService } from "VSS/Platform/History";

import { INode, NodeType } from "./Types";
import { FolderUtils } from "./FolderUtils";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { SelectionActions } from "Release/Landing/Selection/SelectionActions";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { startScenario, PerfScenarios, endScenario } from "Release/Helpers/Performance";

import { Folder, ReleaseDefinition } from "Release/Client/Contracts/Release";
import * as Resources from "Release/Landing/Resources";
import { AllDefinitionsStore, AllPipelinesItemDeleted } from "./AllDefinitionsStore";
import { SearchDefinitionsTreeExProvider } from "./SearchDefinitionsTreeProvider";
import { MessageActions } from "HubMessageBar/MessageActions";
import { MessageBarKeys, QueryParamKeys } from "Constants";
import { getActiveDefinitionReferenceMatch } from "LandingUtils";

import { ActivePipelinesStore } from "../ActivePipelines/ActivePipelinesStore";
import { PipelineSearchStore } from "Release/Landing/LeftPanel/PipelineSearch/PipelineSearchStore";
import { ActivePipelinesActionCreator } from "../ActivePipelines/ActivePipelinesActionCreator";

import "VSS/LoaderPlugins/Css!Release/Landing/LeftPanel/AllPipelines/AllDefinitionsTree";
import { SelectionStore } from "Release/Landing/Selection/SelectionStore";

type ObservableNode = ObservableValue<INode>;

interface IAllDefinitionsTreeState {
    treeExProvider: ITreeItemExProvider<ObservableNode> | null;
    treeProviderLoading: boolean;
    searchTreeExProvider: ITreeItemExProvider<ObservableNode> | null;
    searchText: string;
    searchedDefinitions: ReleaseDefinition[] | null;
    searchResultsLoading: boolean;
}

export class AllDefinitionsTree extends VssComponent<IVssComponentProperties, IAllDefinitionsTreeState, IVssComponentContext> {

    public constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);

        this._selectionActions = ActionsHubManager.GetActionsHub<SelectionActions>(SelectionActions);

        const historyState = context.pageContext.getService<IVssHistoryService>("IVssHistoryService").getState();
        this._initialDefinitionId = (historyState[QueryParamKeys.DefinitionIdKey] && parseInt(historyState[QueryParamKeys.DefinitionIdKey], 10)) || -1;

        let folderPathInUrl = historyState[QueryParamKeys.PathKey] || "";
        if (folderPathInUrl && folderPathInUrl[0] !== '\\') {
            folderPathInUrl = "\\" + folderPathInUrl;
        }

        if (folderPathInUrl && folderPathInUrl !== "\\" && folderPathInUrl[folderPathInUrl.length - 1] === '\\') {
            folderPathInUrl = folderPathInUrl.substring(0, folderPathInUrl.length - 1);
        }

        this._initialFolderId = folderPathInUrl;


        this._store = StoreManager.GetStore<AllDefinitionsStore>(AllDefinitionsStore, "all-def-store", context.pageContext);
        this._pipelineSearchStore = StoreManager.GetStore<PipelineSearchStore>(PipelineSearchStore);

        const pipelineSearchState = this._pipelineSearchStore.getState();
        const searchedDefinitions = pipelineSearchState.searchedDefinitions;
        this.state = {
            treeExProvider: this._store.getDefinitionTreeExProvider(false),
            treeProviderLoading: this._store.isLoadingTreeProvider(),
            searchText: pipelineSearchState.searchText,
            searchResultsLoading: pipelineSearchState.searchResultsLoading,
            searchedDefinitions: searchedDefinitions,
            searchTreeExProvider: new SearchDefinitionsTreeExProvider(this._getFoldersForSearchedDefinitions(searchedDefinitions), searchedDefinitions || [])
        } as IAllDefinitionsTreeState;

        this._treeSelection = new ListSelection();

        this._store.addChangedListener(this._onStoreChange);
        this._store.addListener(AllPipelinesItemDeleted, this._onItemDeleted);

        startScenario(context.pageContext, PerfScenarios.AllPivot, false);
    }

    public componentDidMount(): void {
        this._pipelineSearchStore.addChangedListener(this._onSearchPipelineStoreChange);

        if (!this.state.treeProviderLoading) {
            this._selectTreeItem();

            setTimeout(() => {
                endScenario(this.context.pageContext, PerfScenarios.AllPivot, false);
            }, 0);
        }
    }

    public componentDidUpdate(prevProps: IVssComponentProperties, prevState: IAllDefinitionsTreeState): void {
        this._selectTreeItem();

        if (prevState.treeProviderLoading && !this.state.treeProviderLoading) {
            setTimeout(() => {
                endScenario(this.context.pageContext, PerfScenarios.AllPivot, false);
            }, 0);

        }
    }

    public componentWillUnmount() {
        this._store.removeChangedListener(this._onStoreChange);
        this._pipelineSearchStore.removeChangedListener(this._onSearchPipelineStoreChange);
    }

    public render() {
        this.setTimeout(() => this._updateTreeExSelection(), 0);

        if (this.state.searchText) {
            if (this.state.searchResultsLoading) {
                return <Spinner className={"flex flex-grow loading-active"} size={SpinnerSize.large} />;
            }
            else if (this.state.searchedDefinitions && this.state.searchedDefinitions.length > 0) {
                return (<TreeEx
                    columns={this._treeExColumns}
                    className={"release-pipe-list-search-tree"}
                    showLines={false}
                    selection={this._treeSelection}
                    itemProvider={this.state.searchTreeExProvider!}
                    singleClickActivation={true}
                    onActivate={(event: any, treeRow) => {
                        this._toggleTreeItem(treeRow.data);
                        if (treeRow.data && treeRow.data.underlyingItem) {
                            this._onSelectionChanged([treeRow.data.underlyingItem.data.value]);
                        }
                    }}

                />);
            }
            else {
                return (
                    <MessageBar
                        className={"all-pivot-no-definitions-found-message"}
                        severity={MessageBarSeverity.Info}>
                        {Resources.NoDefinitionsMatchYourFilterText}
                    </MessageBar>);
            }
        }
        else {
            const treeProviderLoading = this._store.isLoadingTreeProvider();
            if (treeProviderLoading) {
                return <Spinner className={"flex flex-grow loading-active"} size={SpinnerSize.large} />;
            }
            else {
                return (<TreeEx
                    columns={this._treeExColumns}
                    className={"release-pipe-list-tree"}
                    showLines={false}
                    selection={this._treeSelection}
                    itemProvider={this.state.treeExProvider!}
                    singleClickActivation={true}
                    onActivate={(event: any, treeRow) => {
                        this._toggleTreeItem(treeRow.data);
                        if (treeRow.data && treeRow.data.underlyingItem) {
                            this._onSelectionChanged([treeRow.data.underlyingItem.data.value]);
                        }
                    }}

                />);
            }
        }
    }

    private _onRenderNameTreeColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITreeItemEx<ObservableNode>>, treeExItem: ITreeItemEx<ObservableNode>): React.ReactNode => {
        const treeItem = treeExItem.underlyingItem;
        if (!treeItem) {
            return null;
        }

        const node = treeItem.data.value;
        if (!node) {
            return null;
        }

        const iconProps: IIconProps = {
            iconName: FolderUtils.isFolder(node) ? "FabricFolderFill" : "Deploy",
            className: css("all-def-treeex-icon ", (FolderUtils.isFolder(node) ? "pipeline-folder-icon" : ""))
        };

        const onToggle = FolderUtils.isFolder(node) ? () => this._toggleTreeItem(treeExItem) : undefined;
        return (
            <SimpleTableCell
                key={"all-def-tree-" + rowIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                contentClassName={"all-def-tree-cell-content"}
                className={"all-def-tree-cell"}
            >
                <TreeExpand
                    expanded={treeItem.expanded}
                    depth={treeExItem.depth}
                    onClick={(e: React.SyntheticEvent) => e.preventDefault()}
                    onToggle={onToggle}
                >
                    <Icon {...iconProps} />
                    <Tooltip overflowOnly={true} text={this._getName(node)}>
                        <div className={"text-ellipsis"}>{this._getName(node)}</div>
                    </Tooltip>
                </TreeExpand>

            </SimpleTableCell>);
    }

    private _toggleTreeItem(treeExItem: ITreeItemEx<ObservableNode>): void {
        if (treeExItem && !treeExItem.underlyingItem.data) {
            return;
        }

        const node = treeExItem.underlyingItem.data.value;

        if (!FolderUtils.isFolder(node)) {
            return;
        }

        if (this.state.searchTreeExProvider && this.state.searchedDefinitions && this.state.searchedDefinitions.length > 0) {
            this.state.searchTreeExProvider.toggle(treeExItem.underlyingItem);
        }
        else if (this.state.treeExProvider) {
            this.state.treeExProvider.toggle(treeExItem.underlyingItem);
        }
    }

    private _getName = (node: INode): string => {
        if (FolderUtils.isFolder(node)) {
            return FolderUtils.getFolderName(node.folder.path);
        }

        return node.releaseDefinition.name;
    }

    private _onSearchPipelineStoreChange = (): void => {
        const pipelineSearchState = this._pipelineSearchStore.getState();
        if (pipelineSearchState.searchResultsLoading) {
            this.setState({
                searchText: pipelineSearchState.searchText,
                searchedDefinitions: [],
                searchResultsLoading: true,
                searchTreeExProvider: new SearchDefinitionsTreeExProvider([], [])
            });
        }
        else {
            const searchResults = pipelineSearchState.searchedDefinitions;
            const folders: Folder[] = this._getFoldersForSearchedDefinitions(searchResults);
            this.setState({
                searchText: pipelineSearchState.searchText,
                searchedDefinitions: searchResults,
                searchResultsLoading: false,
                searchTreeExProvider: new SearchDefinitionsTreeExProvider(folders, searchResults || [])
            });
        }
    }

    private _onStoreChange = (): void => {
        this.setState({
            treeExProvider: this._store.getDefinitionTreeExProvider(true),
            treeProviderLoading: this._store.isLoadingTreeProvider()
        });
    }

    private _onItemDeleted = (): void => {
        setTimeout(() => this._selectItemFromId("\\"), 0);
    }

    private _selectItemFromId(itemId: string, clearMessageBars: boolean = false) {
        if (this.state.treeExProvider) {
            let selectedNode: INode | null = null
            const index = this.state.treeExProvider.value.findIndex((treeItemEx: ITreeItemEx<ObservableNode>) => {
                if (treeItemEx && treeItemEx.underlyingItem && treeItemEx.underlyingItem.data.value) {
                    const node = treeItemEx.underlyingItem.data.value;
                    if (node.folder && node.folder.path === itemId) {
                        selectedNode = node;
                        return true;
                    }
                    else if (node.releaseDefinition && node.releaseDefinition.id && node.releaseDefinition.id.toString() === itemId) {
                        selectedNode = node;
                        return true;
                    }
                }

                return false;
            });

            if (index >= 0) {
                this._treeSelection.select(index);

                // Invoke selection actions with a timeout to prevent action within action
                this.setTimeout(() => this._onSelectionChanged([selectedNode!], clearMessageBars), 0);
            }
        }
    }

    private _selectTreeItem(): void {
        if (this._isRootResolved() && !this.state.treeProviderLoading && !this._selectionComplete) {
            if (this.state.searchText) {
                // No need to select initially if search is in progress
                this._selectionComplete = true;
                return;
            }

            if (this._initialDefinitionId > 0) {
                // On mount check the RD is visible. Only then select
                this._store.isDefinitionIdResolvedChild(this._initialDefinitionId, "\\").then((result) => {
                    if (result) {
                        this._selectItemFromId(this._initialDefinitionId.toString());
                    }
                    else {
                        this._selectItemFromId("\\");
                    }
                });
            }
            else if (this._initialFolderId) {
                const pipelineTreeItems = this.state.treeExProvider ? this.state.treeExProvider.value : [];
                const isInitialFolderPresent = pipelineTreeItems.some(item => item.underlyingItem.data.value.folder.path === this._initialFolderId);

                if (!isInitialFolderPresent) {
                    ActionsHubManager.GetActionsHub<MessageActions>(MessageActions).setMessage.invoke({ key: MessageBarKeys.HubError, value: localeFormat(Resources.FolderNotFoundMessage, this._initialFolderId) });
                }

                this._selectItemFromId(isInitialFolderPresent ? this._initialFolderId : "\\");
            }
            else {
                this._selectItemFromId("\\");
            }

            this._selectionComplete = true;
        }
    }

    private _isRootResolved(): boolean {
        const pathToChildMap = this._store.getState();

        return pathToChildMap.hasOwnProperty("\\");
    }

    private _updateTreeExSelection(): void {
        // Tree and Table do not understand selection of items, and understands only selection of indices. When we collapse or expand a folder, the indices change
        // Hence the handling below
        const selectionState = StoreManager.GetStore<SelectionStore>(SelectionStore).getState(); // selection state in our selection store
        const treeExProvider = this.state.searchText ? this.state.searchTreeExProvider : this.state.treeExProvider;
        if (treeExProvider && (selectionState.folderId || selectionState.id > 0)) {
            const items = treeExProvider.value; // List of items in tree provider
            const selectedIndex = this._treeSelection.value[0]; // selected index in the selection object passed to the tree 
            const indexOfActualSelection = items.map(item => item.underlyingItem.data.value).findIndex(treeNode => {
                if (!treeNode) {
                    // treeNode is undefined is case of loading nodes
                    return false;
                }

                if (selectionState.id > 0) {
                    return treeNode.releaseDefinition && treeNode.releaseDefinition.id === selectionState.id;
                }
                else if (selectionState.folderId) {
                    return treeNode.folder && (treeNode.folder.path || "").toLowerCase() === selectionState.folderId.toLowerCase()
                }

                return false;
            });

            if (indexOfActualSelection < 0) {
                if (selectedIndex && selectedIndex.beginIndex >= 0) {
                    this._treeSelection.clear();
                }
            }
            else {
                if (!selectedIndex || selectedIndex.beginIndex !== indexOfActualSelection) {
                    this._treeSelection.select(indexOfActualSelection);
                }
            }
        }
    }

    private _getFoldersForSearchedDefinitions(searchedRds: ReleaseDefinition[] | null): Folder[] {
        let folders: Folder[] = [];
        let paths = new Set<string>();
        (searchedRds || []).forEach(x => {
            paths.add(x.path);
        });

        paths.forEach(x => {
            folders.push({
                path: x
            } as Folder);
        });

        return folders;
    }

    private _onSelectionChanged = (newItems: INode[], clearMessageBars: boolean = true): void => {
        if (!newItems.length) {
            return;
        }
        const newItem: INode = newItems[0];
        if (newItem.type === NodeType.Folder) {

            this._selectionActions.selectDefinition.invoke({
                id: -1,
                folderId: newItem.folder.path,
                noSelection: false
            });

            if (clearMessageBars) {
                this._clearMessageBars();
            }

            return;
        }

        const releaseDefinition = newItem.releaseDefinition;

        this._selectionActions.selectDefinition.invoke({
            id: releaseDefinition.id,
            folderId: "",
            noSelection: false,
        });

        const activeStoreState = StoreManager.GetStore<ActivePipelinesStore>(ActivePipelinesStore).getState();
        if (!getActiveDefinitionReferenceMatch(activeStoreState.activeDefinitions, releaseDefinition.id) && !getActiveDefinitionReferenceMatch(activeStoreState.recentDefinitions, releaseDefinition.id)) {
            ActionCreatorManager.GetActionCreator<ActivePipelinesActionCreator>(ActivePipelinesActionCreator).handleAdditionalDefSelection(this.context.pageContext, undefined, releaseDefinition);
        }

        if (clearMessageBars) {
            this._clearMessageBars();
        }
    }

    private _clearMessageBars(): void {
        // Clear message bar error on selection change as we are canging context.
        ActionsHubManager.GetActionsHub<MessageActions>(MessageActions).setMessage.invoke({ key: MessageBarKeys.HubError, value: "" });
        ActionsHubManager.GetActionsHub<MessageActions>(MessageActions).setMessage.invoke({ key: MessageBarKeys.HubSuccess, value: "" });
    }

    private get _treeExColumns(): ITreeColumn<ObservableNode>[] {
        return [{
            hierarchical: true,
            id: nameKey,
            name: Resources.Name,
            width: -100,
            minWidth: 100,
            maxWidth: 700,
            renderCell: this._onRenderNameTreeColumn
        } as ITreeColumn<ObservableNode>
        ];
    }

    private _initialDefinitionId: number;
    private _initialFolderId: string;
    private _selectionComplete: boolean = false;
    private _selectionActions: SelectionActions;
    private _store: AllDefinitionsStore;
    private _pipelineSearchStore: PipelineSearchStore;

    private _treeSelection: ListSelection;
}

const nameKey: string = "name";

Components.add("all-definitions-tree", AllDefinitionsTree);
